import { Capacitor } from "@capacitor/core";
import { PushNotifications, Token } from "@capacitor/push-notifications";
import { registerDevice } from "model/deviceSlice";
import { AppDispatch } from "model/rootReducer";

/**
 * Sets up push notifications and registers the device token with the backend
 * @param dispatch Redux dispatch function
 * @returns Promise<void>
 */
export const setupPushNotificationsAndRegisterDevice = async (
  dispatch: AppDispatch
): Promise<void> => {
  // Only setup push notifications on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log("Push notifications not supported on web platform");
    return;
  }

  try {
    // Request permission to use push notifications
    const permissionResult = await PushNotifications.requestPermissions();

    if (permissionResult.receive !== "granted") {
      console.log("Push notification permission not granted");
      return;
    }

    // Register with Apple / Google to receive push via APNS/FCM
    await PushNotifications.register();

    // Set up the registration listener
    await PushNotifications.addListener(
      "registration",
      async (token: Token) => {
        console.log("Push registration success, token: " + token.value);

        try {
          // Generate a device name based on platform
          const platform = Capacitor.getPlatform();
          const deviceName = `${
            platform.charAt(0).toUpperCase() + platform.slice(1)
          } Device`;

          // Register device with backend
          await dispatch(
            registerDevice({
              name: deviceName,
              token: token.value,
            })
          ).unwrap();

          console.log("Device registered successfully with backend");
        } catch (error) {
          console.error("Failed to register device with backend:", error);
        }
      }
    );

    // Set up error listener
    await PushNotifications.addListener(
      "registrationError",
      (error: unknown) => {
        console.error("Error on registration: " + JSON.stringify(error));
      }
    );
  } catch (error) {
    console.error("Error setting up push notifications:", error);
  }
};

/**
 * Checks if device registration should be attempted
 * This can be called after login/signup or app initialization
 */
export const shouldRegisterDevice = (): boolean => {
  // Only register on native platforms
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  // Check if we've already registered this session
  const hasRegisteredThisSession = sessionStorage.getItem("deviceRegistered");
  if (hasRegisteredThisSession) {
    return false;
  }

  return true;
};

/**
 * Marks device as registered for this session
 */
export const markDeviceAsRegistered = (): void => {
  sessionStorage.setItem("deviceRegistered", "true");
};
